import { IDestroyOptions, Point, Sprite, Spritesheet, Text, TextStyle, Texture } from "pixi.js";
import { BasePageView } from "../../../../common/components/BasePageView";
import { RaidResourcePackage } from "../../resources/RaidResourcePackage";
import { FadeScreen } from "../../../../game/components/FadeScreen";
import { ICanvasScaler } from "../../../../../../canvas/ICanvasScaler";
import { TargetExplosionView } from "../../components/target/TargetExplosionView";
import { YouWonHint } from "../../components/target/YouWonHint";
import { PromiseCompletionSource } from "../../../../../../shared/async/PromiseCompletionSource";
import { gsap } from "gsap";
import { CoinsExplowAnimationView } from "../../components/CoinsExplowAnimationView";
import { TickerDelay } from "../../../../../../tools/TickerDelay";
import { RewardContainer } from "../../../../common/components/RewardContainer";
import { CommonResourcesPackage } from "../../../../common/resources/CommonResourcesPackage";
import { SoundsResourcePackageWrapper } from "../../../../common/resources/SoundsResourcePackageWrapper";
import { RaidTarget } from "../../../../game/flow/impl/diceActions/raidAction/RaidTarget";
import { DiceRollRaidGameResultType } from "../../../../game/flow/impl/diceActions/raidAction/DiceRollRaidGameResultType";
import { CoinsTexturePack } from "by/gamefactory/cityversetycoon/scenes/common/components/animations/texturesPacks/CoinsTexturePack";
import { IRaidController } from "../../controllers/IRaidController";
import { RaidTargetsData } from "../../../../game/flow/impl/diceActions/raidAction/RaidTargetsData";
import { AsyncOperationNotification } from "../../../../common/components/AsyncOperationNotification";

export class CardTargetsPage extends BasePageView {
    private static readonly CHOOSE_TARGET_TOOLTIP_TEXT = "Choose a target to\nRaid the building";
    private static readonly CHOOSE_TARGET_TOOLTIP_SCALE = 0.8;
    private static readonly TARGET_SCALE = 1.6;
    private static readonly TARGETS_VIEW_DESCRIPTION = new Array<TargetViewDescription>(
        { percentPosition: new Point(0.36, 0.58) },
        { percentPosition: new Point(0.65, 0.49) },
        { percentPosition: new Point(0.6, 0.34) },
        { percentPosition: new Point(0.36, 0.42) },
        { percentPosition: new Point(0.6, 0.66) }
    );
    private static readonly HINT_ARROWS_TARGET_VIEW_DESCRIPTION = new Array<HintArrowTargetViewDescription>(
        { percentPosition: new Point(0.28, 0.29), angle: 60, alignType: 0 },
        { percentPosition: new Point(0, 0.44), angle: 30, alignType: 0 },
        { percentPosition: new Point(0.84, 0.39), angle: 0, alignType: 1 },
        { percentPosition: new Point(0.19, 0.69), angle: 180, alignType: 1 },
        { percentPosition: new Point(0.67, 0.82), angle: -60, alignType: 0 }
    );

    private readonly commonResources: CommonResourcesPackage;
    private readonly raidResources: RaidResourcePackage;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly raidController: IRaidController;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly isFirstShow: boolean;
    private raidTargetsData: RaidTargetsData;
    private fadeScreen: FadeScreen;
    private choseTargetToolTip: Text;
    private targets: Array<Sprite>;
    private hintArrows: Array<Sprite>;
    private explosionTargetAnimation: TargetExplosionView;
    private coinsExplowAnimation: CoinsExplowAnimationView;
    private youWonHint: YouWonHint;
    private viewCompletionSource: PromiseCompletionSource<RaidTargetsData>;
    private targetIdleAnimation: gsap.core.Timeline;
    private currentAnimation: gsap.core.Timeline;

    public constructor(
        commonResources: CommonResourcesPackage,
        raidResources: RaidResourcePackage,
        soundResources: SoundsResourcePackageWrapper,
        raidController: IRaidController,
        canvasScaler: ICanvasScaler,
        isFirstShow: boolean,
        asyncOperationNotification: AsyncOperationNotification,
        viewCompletionSource: PromiseCompletionSource<RaidTargetsData>
    ) {
        super(canvasScaler);
        this.commonResources = commonResources;
        this.raidResources = raidResources;
        this.soundResources = soundResources;
        this.raidController = raidController;
        this.asyncOperationNotification = asyncOperationNotification;
        this.isFirstShow = isFirstShow;
        this.viewCompletionSource = viewCompletionSource;
        this.targets = [];
        this.hintArrows = [];
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.once("added", this.showComponents.bind(this));
        this.eventMode = "static";
        this.on("pointerup", this.skipAnimations, this);
    }

    protected override build(): void {
        this.fadeScreen = new FadeScreen(0.3, 0, 0.7, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.addChild(this.fadeScreen);
        CardTargetsPage.TARGETS_VIEW_DESCRIPTION.forEach(() => this.addChild(this.raidTargetFactory()));
        if (this.isFirstShow) {
            CardTargetsPage.HINT_ARROWS_TARGET_VIEW_DESCRIPTION.forEach((description) => this.addChild(this.hintArrowFactory(description.alignType)));
            this.choseTargetToolTip = new Text(CardTargetsPage.CHOOSE_TARGET_TOOLTIP_TEXT, new ChooseTooltipTextStyle());
            this.choseTargetToolTip.angle = -12;
            this.choseTargetToolTip.anchor.set(0.5, 0.5);
            this.choseTargetToolTip.scale.set(CardTargetsPage.CHOOSE_TARGET_TOOLTIP_SCALE);
            this.addChild(this.choseTargetToolTip);
        }
        this.explosionTargetAnimation = new TargetExplosionView(this.raidResources.assets_views_raid_atlases_target_light_json as Spritesheet);
        this.addChild(this.explosionTargetAnimation);
        this.coinsExplowAnimation = new CoinsExplowAnimationView(new CoinsTexturePack(this.commonResources).rotationCoins);
        this.addChild(this.coinsExplowAnimation);
        super.build();
    }

    private raidTargetFactory(): Sprite {
        const target = new Sprite(this.raidResources.assets_views_raid_ui_one_target_png);
        target.anchor.set(0.5, 0.5);
        target.scale.set(CardTargetsPage.TARGET_SCALE);
        target.eventMode = "dynamic";
        //TODO egorja: unsubscribe
        target.on("pointerdown", this.targetClickHandle.bind(this, target));
        this.targets.push(target);
        return target;
    }

    private hintArrowFactory(alignType: ArrowAlignType): Sprite {
        let arrowTexture: Texture = alignType === ArrowAlignType.Left ? this.commonResources.assets_common_ui_hint_arrow_left_png
            : this.commonResources.assets_common_ui_hint_arrow_right_png;
        let anchor: Point = alignType === ArrowAlignType.Left ? new Point(1, 0) : new Point(0, 1);
        const arrow = new Sprite(arrowTexture);
        arrow.anchor.set(anchor.x, anchor.y);
        this.hintArrows.push(arrow);
        return arrow;
    }

    private async showComponents(): Promise<void> {
        this.interactiveTargets(false);
        this.explosionTargetAnimation.visible = false;
        this.targetIdleAnimation = new gsap.core.Timeline();
        this.targets.forEach(target => {
            this.targetIdleAnimation.fromTo(target.scale, { x: 0, y: 0 }, { x: CardTargetsPage.TARGET_SCALE, y: CardTargetsPage.TARGET_SCALE, duration: 0.2, onStart: () => this.soundResources.assets_sounds_targets_Appear_mp3.play() }, "<0.08");
            this.targetIdleAnimation.fromTo(target, { angle: 360 }, { angle: 0, duration: 2.4, repeat: -1, ease: "none" }, "<");
        });
        const animationTimeLine = new gsap.core.Timeline();
        if (this.isFirstShow) {
            animationTimeLine.fromTo(this.fadeScreen, { alpha: 0 }, { alpha: 0.7, duration: 0.3 });
            animationTimeLine.fromTo(this.choseTargetToolTip.scale, { x: 0, y: 0 }, { x: CardTargetsPage.CHOOSE_TARGET_TOOLTIP_SCALE, y: CardTargetsPage.CHOOSE_TARGET_TOOLTIP_SCALE, duration: 0.3, ease: "back.out(2)" });
            this.hintArrows.forEach(arrow => {
                animationTimeLine.fromTo(arrow.scale, { x: 0, y: 0 }, { x: arrow.scale.x, y: arrow.scale.y, duration: 0.1, ease: "back.out(2)" }, "<50%");
            });
        }
        await animationTimeLine;
        animationTimeLine.kill()
        this.interactiveTargets(true);
    }

    private async targetClickHandle(target: Sprite): Promise<void> {
        this.interactiveTargets(false);
        this.raidTargetsData = await this.asyncOperationNotification.autoShowHide(this.raidController.getRaidTargetsData());
        this.targetIdleAnimation?.kill();
        if (this.raidTargetsData.diceRollRaidGameResult == DiceRollRaidGameResultType.COMPENSATION_WIN) {
            this.viewCompletionSource.complete(this.raidTargetsData);
            return;
        }
        await this.hideComponents(target);
        //TODO: Add animation placeholder into target
        this.explosionTargetAnimation.visible = true;
        this.explosionTargetAnimation.position.set(target.position.x, target.position.y + target.height * 0.8);
        this.explosionTargetAnimation.play();
        this.soundResources.assets_sounds_Raid_Correct_Target_mp3.play();
        if (this.isFirstShow) {
            await TickerDelay.delay(TargetExplosionView.EXPLOSION_DURATION);
            this.explosionTargetAnimation.stop();
        }
        this.coinsExplowAnimation.position.copyFrom(target.position);
        this.coinsExplowAnimation.play();
        if (this.isFirstShow) {
            await TickerDelay.delay(CoinsExplowAnimationView.BLOW_EMIT_DURATION);
            this.coinsExplowAnimation.stop();
        }
        await this.startExplosionTarget(target);
        await Promise.all([
            this.explosionTargetAnimation.continue(),
            this.coinsExplowAnimation.continue()
        ]);
        this.coinsExplowAnimation.destroy();
        this.explosionTargetAnimation.destroy();
        await this.showOtherTargetWithReward(target);
        await TickerDelay.delay(400);
        this.viewCompletionSource.complete(this.raidTargetsData);
    }

    private async showOtherTargetWithReward(target: Sprite): Promise<void> {
        const animationTimeLine = this.currentAnimation = new gsap.core.Timeline();
        let targetsData: Array<RaidTarget> = [];
        targetsData.push(...this.raidTargetsData.diceRollRaidTargets);
        const willBeChosenIndex = targetsData.findIndex((item) => item.willBeChosen === true);
        if (willBeChosenIndex != -1) {
            targetsData.splice(willBeChosenIndex, 1);
        }
        let index = 0;
        this.targets.forEach(item => {
            if (item != target) {
                item.angle = 0;
                const rewardContainer = new RewardContainer(this.raidResources.assets_views_raid_ui_coin_icon_png, targetsData[index].winAmountInCoins);
                item.addChild(rewardContainer);
                animationTimeLine.fromTo(item.scale, { x: 0, y: 0 }, { x: CardTargetsPage.TARGET_SCALE, y: CardTargetsPage.TARGET_SCALE, duration: 0.2 }, "<60%");
                index++;
            }
        });
        await animationTimeLine;
        animationTimeLine.kill();
    }

    private async startExplosionTarget(target: Sprite): Promise<void> {
        this.updateTarget(target);
        const rewardValue = this.raidTargetsData.diceRollRaidTargets.find((item) => item.willBeChosen === true).winAmountInCoins;
        const rewardContainer = new RewardContainer(this.raidResources.assets_views_raid_ui_coin_icon_png, rewardValue);
        rewardContainer.scale.set(1.5);
        rewardContainer.position = target.position;
        this.addChild(rewardContainer);
        const animationTimeLine = this.currentAnimation = new gsap.core.Timeline();
        animationTimeLine.fromTo(rewardContainer.scale, { x: 0, y: 0 }, { x: rewardContainer.scale.x, y: rewardContainer.scale.y, duration: 0.15, ease: "back.out(2)" });
        if (this.isFirstShow) {
            this.fadeScreen.fadeIn();
        }
        animationTimeLine.add(this.createYouWonHint(new Point(target.x, target.y), rewardValue)?.play());
        if (this.youWonHint != null) {
            animationTimeLine.to(this.youWonHint.scale, { x: 0, y: 0, duration: 0.3, delay: 0.5, ease: "back.in(2)" });
        }
        await animationTimeLine;
        animationTimeLine.kill();
        if (this.isFirstShow) {
            this.fadeScreen.fadeOut();
        }
    }

    private updateTarget(target: Sprite): void {
        target.texture = this.raidResources.assets_views_raid_ui_fiery_exploding_burst_png;
        target.scale.set(0.35, 0.35);
    }

    private createYouWonHint(position: Point, rewardValue: number): gsap.core.Timeline {
        this.youWonHint = new YouWonHint(this.commonResources, rewardValue);
        const isOutOffsideHint = position.x - (this.youWonHint.width - 100) < 0;
        this.youWonHint.position.set(position.x += isOutOffsideHint ? 200 : -200, position.y + 200);
        this.youWonHint.updateArrowAlign(isOutOffsideHint);
        this.addChild(this.youWonHint);
        if (this.isFirstShow) {
            return this.youWonHint.playAnimations();
        }
    }

    private async hideComponents(target: Sprite): Promise<void> {
        const animationTimeLine = this.currentAnimation = new gsap.core.Timeline();
        if (this.isFirstShow) {
            animationTimeLine.to(this.fadeScreen, { alpha: 0, duration: 0.2 });
            animationTimeLine.to(this.choseTargetToolTip.scale, { x: 0, y: 0, duration: 0.15, ease: "back.in(2)" }, "<");
        }
        this.targets.forEach(item => {
            if (item != target) {
                animationTimeLine.to(item.scale, { x: 0, y: 0, duration: 0.15 }, "<");
            }
        })
        if (this.isFirstShow) {
            this.hintArrows.forEach(arrow => {
                animationTimeLine.set(arrow.scale, { x: 0, y: 0 }, "<");
            });
        }
        await animationTimeLine;
        animationTimeLine.kill();
    }

    private skipAnimations(): void {
        if (this.currentAnimation != null && this.currentAnimation.isActive()) {
            this.currentAnimation.progress(1);
        }
        if (this.explosionTargetAnimation != null && this.explosionTargetAnimation.isActive) {
            this.explosionTargetAnimation.finishProgress();
        }
        if (this.coinsExplowAnimation != null && this.coinsExplowAnimation.isActive) {
            this.coinsExplowAnimation.setProgress(1);
        }
    }

    protected override layout(width: number, height: number): void {
        this.fadeScreen.width = width;
        this.fadeScreen.height = height;
        this.targetsLayout(width, height);
        if (this.isFirstShow) {
            this.hintArrowsLayout(width, height);
            this.choseTargetToolTip.position.set(width * 0.35, height * 0.25);
        }
        this.explosionTargetAnimation.position.set(width * 0.5, height * 0.5);
        this.coinsExplowAnimation.position.set(width * 0.5, height * 0.5);
        super.layout(width, height);
    }

    private targetsLayout(width: number, height: number): void {
        this.targets.map((target, index) => {
            target.scale.set(2, 2);
            const percentPosition = CardTargetsPage.TARGETS_VIEW_DESCRIPTION[index].percentPosition;
            target.position.set(width * percentPosition.x, height * percentPosition.y);
        });
    }

    private hintArrowsLayout(width: number, height: number): void {
        this.hintArrows.map((arrow, index) => {
            const description = CardTargetsPage.HINT_ARROWS_TARGET_VIEW_DESCRIPTION[index];
            arrow.scale.set(description.alignType === ArrowAlignType.Left ? 1 : 1.2)
            arrow.position.set(width * description.percentPosition.x, height * description.percentPosition.y);
            arrow.angle = description.angle;
        });
    }

    private interactiveTargets(isLock: boolean): void {
        this.targets.forEach(target => target.eventMode = isLock ? "dynamic" : "none");
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.off("pointerup", this.skipAnimations, this);
        this.targets.forEach(target => target.off("pointerdown"));
        super.destroy(options);
    }
}

type TargetViewDescription = {
    percentPosition: Point;
}

type HintArrowTargetViewDescription = {
    percentPosition: Point;
    angle: number;
    alignType: ArrowAlignType;
}

enum ArrowAlignType {
    Right = 0,
    Left = 1
}

class ChooseTooltipTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fontFamily: "Grapenuts Regular",
            fill: "#ffffff",
            fontSize: 80,
            align: "right"
        }));
    }
}
